import { Declaration, DeclarationBasis, DeclarationFilterField, DeclarationFilters } from '../types/declaration';


export const DECLARATION_BASES: DeclarationBasis[] = ['2425', '353'];


export const isDeclarationBasis = (basis: string | undefined): boolean => {
    return basis === '2425' || basis === '353';
};


// Значение поля legal_basis в API: "ПП 2425", "ПП 353"
export const getLegalBasis = (basis: DeclarationBasis): string => {
    return `ПП ${basis}`;
};


export const getDeclarationDetailPath = (basis: DeclarationBasis, regNumber: string): string => {
    return `/declarations/${basis}/${encodeURIComponent(regNumber)}`;
};


export const getDeclarationStatus = (declaration: Declaration): string => {
    const status = (declaration.status || '').trim();
    if (!status) return 'Не указан';
    return status.charAt(0).toUpperCase() + status.slice(1);
};


// Цвета статусов как на диаграмме статусов обязательных сертификатов
const STATUS_COLORS: { [key: string]: string } = {
    'действует': 'rgba(126, 178, 70, 1)',
    'прекращен': 'rgba(237, 93, 98, 1)',
    'прекращён': 'rgba(237, 93, 98, 1)',
    'архивный': 'rgba(247, 0, 150, 1)',
    'недействителен': 'rgba(0, 0, 0, 1)',
    'приостановлен': 'rgba(255, 166, 0, 1)',
    'возобновлен': 'rgba(0, 113, 188, 1)',
    'возобновлён': 'rgba(0, 113, 188, 1)',
    'не указан': 'rgba(200, 200, 200, 1)',
};

// Для статусов, которых нет в списке выше
const OTHER_STATUS_COLORS = [
    'rgba(111, 66, 193, 1)',
    'rgba(23, 162, 184, 1)',
    'rgba(108, 117, 125, 1)',
    'rgba(153, 102, 51, 1)',
];


export const getStatusColor = (status: string, index: number = 0): string => {
    const color = STATUS_COLORS[status.toLowerCase()];
    if (color) return color;
    return OTHER_STATUS_COLORS[index % OTHER_STATUS_COLORS.length];
};


// Количество деклараций по каждому статусу, по убыванию
export const getStatusCounts = (declarations: Declaration[]): { status: string; count: number }[] => {
    const counts = new Map<string, number>();
    declarations.forEach(declaration => {
        const status = getDeclarationStatus(declaration);
        counts.set(status, (counts.get(status) || 0) + 1);
    });
    return Array.from(counts.entries())
        .map(([status, count]) => ({ status, count }))
        .sort((first, second) => second.count - first.count);
};


export const getRegYear = (declaration: Declaration): string => {
    return declaration.reg_date ? declaration.reg_date.substring(0, 4) : '';
};


// Ссылка на декларацию в реестре ФСА: из парсера, а если её нет - по fsa_id
export const getFsaLink = (declaration: Declaration): string | null => {
    if (declaration.link && declaration.link.startsWith('http')) return declaration.link;
    if (declaration.fsa_id) return `https://pub.fsa.gov.ru/rds/declaration/view/${declaration.fsa_id}/common`;
    return null;
};


// Парсер пишет лаборатории одной строкой через ";"
export const getLabNames = (declaration: Declaration): string[] => {
    if (!declaration.lab_names) return [];
    return declaration.lab_names
        .split(';')
        .map(name => name.trim())
        .filter(name => name && name.toLowerCase() !== 'отсутствует');
};


export const getApplicantName = (declaration: Declaration): string => {
    return declaration.applicant_short_name || declaration.applicant_full_name || '';
};


export const getEmptyDeclarationFilters = (): DeclarationFilters => ({
    fields: {
        reg_number: [],
        product_kind: [],
        applicant_name: [],
        applicant_inn: [],
        manufacturer_full_name: [],
        manufacturer_inn: [],
        lab_names: []
    },
    statuses: [],
    years: [],
    regDate: { start: '', end: '' },
    endDate: { start: '', end: '' }
});


// Значения декларации, по которым ищет фильтр (лабораторий в декларации может быть несколько)
export const getFilterFieldValues = (declaration: Declaration, field: DeclarationFilterField): string[] => {
    if (field === 'applicant_name') {
        const applicantName = getApplicantName(declaration);
        return applicantName ? [applicantName] : [];
    }
    if (field === 'lab_names') {
        return getLabNames(declaration);
    }
    const value = declaration[field];
    return value ? [value] : [];
};
